import { useState } from 'react'
import { FileUp, Upload } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  CUSTOMER_IMPORT_TEMPLATE,
  downloadCsvTemplate,
  importCustomersCsv,
  importServicesCsv,
  SERVICE_IMPORT_TEMPLATE,
} from '@/lib/api/import'
import { cn } from '@/lib/utils'

const TABS = [
  { id: 'customers', label: 'Customers' },
  { id: 'services', label: 'Services' },
]

export function ImportMastersPage() {
  const [activeTab, setActiveTab] = useState('customers')
  const [file, setFile] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [summary, setSummary] = useState(null)

  async function handleSubmit(event) {
    event.preventDefault()
    if (!file) {
      setError('Choose a CSV file to import.')
      return
    }

    setSubmitting(true)
    setError('')
    setSummary(null)

    try {
      const result =
        activeTab === 'customers'
          ? await importCustomersCsv(file)
          : await importServicesCsv(file)
      setSummary(result)
      setFile(null)
      event.currentTarget.reset()
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Import failed.')
    } finally {
      setSubmitting(false)
    }
  }

  const template =
    activeTab === 'customers' ? CUSTOMER_IMPORT_TEMPLATE : SERVICE_IMPORT_TEMPLATE
  const templateName =
    activeTab === 'customers' ? 'customers-import-template.csv' : 'services-import-template.csv'

  return (
    <div className="mx-auto max-w-[900px] px-4 py-8 lg:px-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Upload className="h-5 w-5" aria-hidden />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">CSV import</h1>
          <p className="text-sm text-muted-foreground">
            Bulk onboard customers and services for the active branch.
          </p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              setActiveTab(tab.id)
              setFile(null)
              setSummary(null)
              setError('')
            }}
            className={cn(
              'rounded-full px-4 py-2 text-sm font-medium transition-colors',
              activeTab === tab.id ? 'bg-primary text-primary-foreground' : 'bg-muted/70 hover:bg-muted',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="card-surface space-y-5 p-6 lg:p-8">
        <div className="rounded-xl bg-muted/50 p-4 text-sm text-muted-foreground">
          <p className="mb-2 font-medium text-foreground">Template columns</p>
          <pre className="overflow-x-auto whitespace-pre-wrap text-xs">{template}</pre>
          <Button
            type="button"
            variant="outline"
            className="mt-3"
            onClick={() => downloadCsvTemplate(templateName, template)}
          >
            <FileUp className="h-4 w-4" />
            Download template
          </Button>
        </div>

        <div>
          <label htmlFor="import-file" className="mb-1.5 block text-sm font-medium">
            CSV file
          </label>
          <input
            id="import-file"
            type="file"
            accept=".csv,text/csv,text/plain"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className={cn(
              'block w-full rounded-xl border border-border/80 bg-card px-4 py-3 text-sm',
              'file:mr-4 file:rounded-lg file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary',
            )}
            required
          />
        </div>

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        {summary ? (
          <div className="rounded-xl border border-border/80 p-4 text-sm">
            <p className="font-medium">Import summary</p>
            <p className="mt-2 text-muted-foreground">
              Created: {summary.created} · Updated: {summary.updated} · Failed: {summary.failed}
            </p>
            {summary.errors?.length ? (
              <ul className="mt-3 space-y-1 text-destructive">
                {summary.errors.map((item) => (
                  <li key={`${item.line}-${item.message}`}>
                    Line {item.line}: {item.message}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        <Button type="submit" disabled={submitting}>
          {submitting ? 'Importing…' : `Import ${activeTab}`}
        </Button>
      </form>
    </div>
  )
}
